import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fantasyService } from "@/api/services";
import { Crown, Lightning, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * Public, anonymous, single-card AI captain pick for the current FPL
 * gameweek. Server caches one LLM call per gameweek so this widget is
 * effectively free for SEO traffic. Renders nothing if FPL isn't running
 * (off-season / advisor temporarily down).
 */
export default function PublicCaptainOfTheWeek() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fantasyService.captainOfTheWeek();
                if (!cancelled) setData(res);
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (error) return null; // Quietly hide on off-season / advisor failure.

    const cap = data?.advice?.captain;
    const diff = data?.advice?.differential;
    const avoid = data?.advice?.avoid;
    const gw = data?.gameweek;
    const deadline = gw?.deadline
        ? new Date(gw.deadline).toLocaleString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : null;

    return (
        <section
            className="max-w-7xl mx-auto px-4 md:px-8 py-16"
            data-testid="public-cotw"
        >
            <div className="flex items-baseline justify-between mb-6 border-b border-border pb-3">
                <div>
                    <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-1">
                        ▌ FPL · AI captain pick
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                        {gw ? gw.name : "Captain of the week"}
                    </h2>
                </div>
                {deadline && (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hidden md:block">
                        Deadline · {deadline}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="bg-card border border-border p-10 animate-pulse">
                    <div className="h-3 w-32 bg-border mb-4" />
                    <div className="h-12 w-1/2 bg-border" />
                </div>
            ) : !cap ? (
                <div className="bg-card border border-border p-6 text-zinc-500 font-mono text-sm">
                    AI captain pick isn't available right now — check back closer to
                    the next deadline.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
                    {/* Hero captain card */}
                    <article
                        className="bg-card border border-primary/40 p-6 md:p-8 relative overflow-hidden"
                        data-testid="public-cotw-captain"
                    >
                        <Crown
                            size={140}
                            weight="duotone"
                            className="absolute -right-6 -top-6 text-primary/10"
                        />
                        <div className="relative">
                            <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-primary text-primary mb-3">
                                Captain
                            </span>
                            <h3 className="text-4xl md:text-5xl font-black tracking-tight">
                                {cap.name}
                            </h3>
                            <div className="font-mono text-xs uppercase tracking-widest text-zinc-400 mt-2">
                                {cap.team}
                                {cap.ownership != null && ` · ${cap.ownership}% owned`}
                                {cap.form != null && ` · form ${cap.form}`}
                            </div>
                            <p className="text-base md:text-lg text-zinc-200 mt-5 leading-relaxed max-w-2xl">
                                {cap.reason}
                            </p>
                            {data?.advice?.headline && (
                                <p className="text-sm text-zinc-400 mt-4 italic">
                                    {data.advice.headline}
                                </p>
                            )}
                            <div className="border-t border-border mt-6 pt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                    Want this analysis for <span className="text-primary">your</span> squad?
                                </p>
                                <div className="flex items-center gap-3">
                                    {gw?.id && (
                                        <Link
                                            to={`/fpl/gw${gw.id}/captain`}
                                            className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-primary transition-snappy"
                                            data-testid="public-cotw-detail-link"
                                        >
                                            Read full analysis →
                                        </Link>
                                    )}
                                    <Link
                                        to="/fantasy"
                                        className="inline-flex items-center gap-2 font-bold uppercase tracking-wider text-sm bg-primary text-primary-foreground hover:bg-[#33FFB0] px-4 py-2 transition-snappy"
                                        data-testid="public-cotw-cta"
                                    >
                                        Get personalised picks
                                        <ArrowRight size={14} weight="bold" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </article>

                    {/* Side cards */}
                    <div className="space-y-4">
                        {diff && (
                            <article
                                className="bg-card border border-border p-5"
                                data-testid="public-cotw-differential"
                            >
                                <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-amber-500/60 text-amber-300 mb-2">
                                    Differential
                                </span>
                                <div className="flex items-baseline justify-between">
                                    <h4 className="text-xl font-bold">{diff.name}</h4>
                                    {diff.ownership != null && (
                                        <span className="font-mono text-xs text-amber-300">
                                            {diff.ownership}%
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                                    {diff.team}
                                </div>
                                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                                    {diff.reason}
                                </p>
                            </article>
                        )}

                        {avoid && (
                            <article
                                className="bg-card border border-border p-5"
                                data-testid="public-cotw-avoid"
                            >
                                <span className="inline-block font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-500/40 text-red-300 mb-2">
                                    Avoid
                                </span>
                                <h4 className="text-xl font-bold">{avoid.name}</h4>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                                    {avoid.team}
                                </div>
                                <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                                    {avoid.reason}
                                </p>
                            </article>
                        )}

                        <article className="bg-card border border-border p-5 flex items-start gap-3">
                            <Sparkle
                                size={20}
                                weight="fill"
                                className="text-primary shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-zinc-300 leading-relaxed">
                                The AI picks <span className="text-primary font-semibold">your</span> captain,
                                transfers, chip strategy and differentials when you link your FPL
                                team ID — VIP only.
                            </p>
                        </article>
                    </div>
                </div>
            )}
        </section>
    );
}
